import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowRight,
  CheckCircle,
  Droplets,
  Loader2,
  MapPin,
  Package,
  RotateCcw,
  Search,
  SearchX,
  UserCheck,
  X,
  XCircle,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import useModalBackButton from '../hooks/useModalBackButton';
import { safeFetch } from '../utils/safeFetch';
import { STAGE_BADGE_STYLES, STAGES_CONFIG } from '../utils/stages';
import { saveOrderLookupReturnContext } from '../utils/orderLookupReturn';
import { haptic } from '../utils/vibration';
import FloatingField from './FloatingField';
import ModalShell from './ModalShell';
import { ModalListSkeleton } from './Skeleton';
import WindowModalHeader from './WindowModalHeader';
import { useOffline } from '../contexts/OfflineContext';
import { readOfflineRoutesSnapshot, shouldUseOfflineDetailFallback } from '../utils/offlineRoutesCache';

const STATUS_STYLES = {
  unassigned: {
    dotClassName: 'bg-gray-500',
    orderBadgeClassName: 'bg-gray-100 text-gray-700 ring-gray-300 dark:bg-gray-900/30 dark:text-gray-300 dark:ring-gray-900/40',
  },
  active: {
    dotClassName: 'bg-blue-500',
    orderBadgeClassName: 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-900/40',
  },
  deferred: {
    dotClassName: 'bg-amber-500',
    orderBadgeClassName: 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-900/40',
  },
  revision_active: {
    dotClassName: 'bg-orange-500',
    orderBadgeClassName: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:ring-orange-900/40',
  },
  completed: {
    dotClassName: 'bg-green-500',
    orderBadgeClassName: 'bg-green-100 text-green-700 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-900/40',
  },
  cancelled: {
    dotClassName: 'bg-red-500',
    orderBadgeClassName: 'bg-red-100 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-900/40',
  },
};

const formatAssigneeShortName = (value) => {
  const text = String(value || '').trim();
  if (!text) return 'Не назначен';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return text;
  const [lastName, firstName = '', middleName = ''] = parts;
  const firstInitial = firstName ? `${firstName[0].toUpperCase()}.` : '';
  const middleInitial = middleName ? `${middleName[0].toUpperCase()}.` : '';
  return `${lastName} ${firstInitial}${middleInitial}`.trim();
};

const normalizeOrderNumber = (value) => (
  String(value || '').trim().replace(/^№\s*/i, '')
);

const FALLBACK_STAGE_CONFIGS = {
  new: { icon: Package, color: 'blue' },
  completed: { icon: CheckCircle, color: 'green' },
  cancelled: { icon: XCircle, color: 'rose' },
};

const OrderLookupModal = ({ open, onClose, onValidationError }) => {
  const offline = useOffline();
  const navigate = useNavigate();
  const location = useLocation();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [offlineSearch, setOfflineSearch] = useState(false);

  useModalBackButton(open, onClose);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setResult(null);
    setError('');
    setHasSearched(false);
    setOfflineSearch(false);
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, typeof window !== 'undefined' && window.innerWidth >= 640 ? 50 : 250);
    return () => window.clearTimeout(timer);
  }, [open]);

  const handleSearch = async (event) => {
    event?.preventDefault?.();
    const orderNumber = normalizeOrderNumber(query);
    if (!orderNumber) {
      setError('');
      setResult(null);
      setHasSearched(false);
      if (onValidationError) {
        onValidationError('Введите номер заказа');
      } else {
        haptic('error');
      }
      inputRef.current?.focus();
      return;
    }

    haptic('tap');
    setLoading(true);
    setError('');
    setResult(null);
    setHasSearched(true);
    setOfflineSearch(false);
    try {
      if (navigator.onLine === false || ['offline', 'server_unavailable'].includes(offline?.networkStatus)) throw new Error('Нет соединения с сервером');
      const data = await safeFetch(
        `${API_URL}/api/orders/lookup?order_number=${encodeURIComponent(orderNumber)}`
      );
      setResult(data || null);
    } catch (searchError) {
      if (shouldUseOfflineDetailFallback(searchError)) {
        const saved = await readOfflineRoutesSnapshot().catch(() => null);
        const order = saved?.orderDetails?.find(item => normalizeOrderNumber(item.order_number).toLowerCase() === orderNumber.toLowerCase());
        setOfflineSearch(true);
        if (order) {
          const activeRework = Boolean(order.active_rework);
          const reworkCount = Array.isArray(order.items)
            ? order.items.filter((it) => it.requires_rework).length
            : Number(order.rework_items_count || 0);
          setResult({
            order_id: order.id,
            order_number: order.order_number,
            stage_name: order.status,
            stage_label: STAGES_CONFIG[order.status]?.label || order.status,
            assignee_name: order.assignee_name,
            lifecycle_status: order.lifecycle_status,
            lifecycle_label: order.lifecycle_label,
            address: ['delivery', 'handover'].includes(order.status) ? order.delivery_address_text : order.pickup_address_text,
            flows: Array.isArray(order.flows) ? order.flows : [],
            active_rework: activeRework,
            rework_items_count: reworkCount,
          });
        }
        return;
      }
      setError(searchError.message || 'Не удалось выполнить поиск');
    } finally {
      setLoading(false);
    }
  };

  const flows = Array.isArray(result?.flows) ? result.flows : [];
  const isReworkDryingOrder = result?.stage_name === 'drying_packing' && Boolean(result?.active_rework);
  const statusStyles = STATUS_STYLES[result?.lifecycle_status] || STATUS_STYLES.active;
  const hideAssigneeRow = ['completed', 'cancelled'].includes(result?.lifecycle_status);

  const stageConfig = STAGES_CONFIG[result?.stage_name]
    || FALLBACK_STAGE_CONFIGS[result?.stage_name]
    || { icon: Package, color: 'gray' };
  const StageIcon = stageConfig.icon;
  const stageBadgeClassName = STAGE_BADGE_STYLES[stageConfig.color] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';

  const openOrder = () => {
    if (!result?.order_id) return;
    haptic('selection');
    saveOrderLookupReturnContext(location);
    onClose();
    navigate(`/order/${result.order_id}`);
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      variant="window"
      swipeToClose={false}
      containerClassName="!items-start !pb-0 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-4"
      panelClassName="flex max-h-[min(82vh,620px)] flex-col overflow-hidden p-0 sm:max-w-md"
      panelProps={{
        role: 'dialog',
        'aria-modal': true,
        'aria-labelledby': 'order-lookup-modal-title',
        style: { padding: 0 },
      }}
    >
      <WindowModalHeader
        icon={Search}
        title="Поиск заказа"
        titleId="order-lookup-modal-title"
        onClose={onClose}
        closeLabel="Закрыть поиск"
      />

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {offlineSearch && <p className="mb-3 text-xs text-amber-700 dark:text-amber-300">Поиск среди сохранённых заказов на этом устройстве</p>}
        <form onSubmit={handleSearch} className="flex gap-2">
          <FloatingField label="Номер заказа" className="min-w-0 flex-1" error={!!error}>
            <div className="relative">
              <input
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                enterKeyHint="search"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value.replace(/\D/g, ''));
                  setError('');
                }}
                placeholder="Номер заказа"
                autoComplete="off"
                className="h-11 w-full rounded-xl bg-white px-3 pr-10 text-sm text-gray-900 outline-none transition-colors dark:bg-gray-800 dark:text-gray-100"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => {
                    setQuery('');
                    setResult(null);
                    setError('');
                    setHasSearched(false);
                    inputRef.current?.focus();
                  }}
                  className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                  aria-label="Очистить номер"
                  title="Очистить"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </FloatingField>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            aria-label="Найти заказ"
            title="Найти заказ"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          </button>
        </form>

        {error && (
          <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="min-h-48 py-4">
            <ModalListSkeleton count={2} />
          </div>
        ) : result ? (
          <div className="mt-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
            <div className="px-4 pb-3 pt-2">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1">
                <div className="flex items-center gap-1.5">
                  <span className={`inline-flex h-6 w-fit items-center rounded-md px-1.5 text-[11px] font-medium leading-none ring-1 ${statusStyles.orderBadgeClassName}`}>
                    № {result.order_number}
                  </span>
                  {result.active_rework && (
                    <span
                      className="inline-flex h-6 items-center gap-1 rounded-md bg-amber-100 px-1.5 text-[11px] font-medium text-amber-800 ring-1 ring-amber-300 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800/60"
                      title={Number(result.rework_items_count || 0) > 0 ? `На доработке: ${result.rework_items_count}` : 'На доработке'}
                    >
                      <RotateCcw className="h-3 w-3" />
                      {Number(result.rework_items_count || 0) > 0 && (
                        <span>{result.rework_items_count}</span>
                      )}
                    </span>
                  )}
                </div>
                <div className="flex h-6 items-center justify-end gap-1.5 text-right text-[12px] font-medium leading-4 text-gray-900 dark:text-gray-100">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${statusStyles.dotClassName}`} />
                  <span>{result.lifecycle_label}</span>
                </div>
              </div>

              <div className="mt-2 border-t border-gray-200 dark:border-gray-700" />

              <div className="mt-3 space-y-2">
                <div className="flex min-h-8 items-center gap-2 text-[12px] font-medium text-gray-950 dark:text-gray-50">
                  <span className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${stageBadgeClassName}`}>
                    <StageIcon className="h-3.5 w-3.5" />
                  </span>
                  <span className="truncate">{result.stage_label || 'Этап не указан'}</span>
                </div>

                <div className="flex items-start gap-1 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50">
                  <MapPin className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                  <span className="break-words">{result.address || 'Адрес не указан'}</span>
                </div>

                {!hideAssigneeRow && (
                  <div className="space-y-1.5 pt-0.5">
                    {isReworkDryingOrder ? (
                      <>
                        <div className="flex items-center gap-1.5 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50">
                          <Package className={`h-3 w-3 shrink-0 ${result.assignee_name ? 'text-purple-600 dark:text-purple-400' : 'text-orange-700 dark:text-orange-300'}`} />
                          <span className={result.assignee_name ? 'text-gray-700 dark:text-gray-300' : 'text-orange-800 dark:text-orange-200'}>
                            Сушка: {formatAssigneeShortName(result.assignee_name)}
                          </span>
                        </div>
                        {flows.map((flow) => {
                          const flowConfig = STAGES_CONFIG[flow.stage_name] || STAGES_CONFIG.washing;
                          const FlowIcon = flowConfig.icon || UserCheck;
                          const flowLabel = flow.stage_label || (flow.stage_name === 'washing_cleaning' ? 'Чистка' : 'Стирка');
                          const hasAssignee = !!String(flow.assignee_name || '').trim();
                          const isFlowCompleted = flow.stage_status === 'completed';

                          return (
                            <div
                              key={flow.stage_name}
                              className="flex items-center justify-between gap-2 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50"
                            >
                              <div className={`flex min-w-0 items-center gap-1.5 ${hasAssignee ? 'text-amber-800 dark:text-amber-300' : 'text-orange-800 dark:text-orange-200'}`}>
                                <span className="relative flex shrink-0 items-center">
                                  <FlowIcon className={`h-3 w-3 ${hasAssignee ? 'text-amber-600 dark:text-amber-400' : 'text-orange-700 dark:text-orange-300'}`} />
                                  <RotateCcw className={`absolute -bottom-0.5 -right-1 h-2 w-2 ${hasAssignee ? 'text-amber-600 dark:text-amber-400' : 'text-orange-700 dark:text-orange-300'}`} />
                                </span>
                                <span className="truncate">
                                  {flowLabel}: {formatAssigneeShortName(flow.assignee_name)}
                                </span>
                              </div>
                              {isFlowCompleted && (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-950/30 dark:text-green-300">
                                  <CheckCircle className="h-2.5 w-2.5" />
                                  <span>Завершено</span>
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </>
                    ) : flows.length > 0 ? (
                      flows.map((flow) => {
                        const flowConfig = STAGES_CONFIG[flow.stage_name] || STAGES_CONFIG.washing;
                        const FlowIcon = flowConfig.icon || UserCheck;
                        const flowLabel = flow.stage_label || (flow.stage_name === 'washing_cleaning' ? 'Чистка' : 'Стирка');
                        const hasAssignee = !!String(flow.assignee_name || '').trim();
                        const isFlowCompleted = flow.stage_status === 'completed';

                        return (
                          <div
                            key={flow.stage_name}
                            className="flex items-center justify-between gap-2 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50"
                          >
                            <div className={`flex min-w-0 items-center gap-1.5 ${hasAssignee ? 'text-gray-700 dark:text-gray-300' : 'text-orange-800 dark:text-orange-200'}`}>
                              <FlowIcon className={`h-3 w-3 shrink-0 ${hasAssignee ? 'text-blue-600 dark:text-blue-400' : 'text-orange-700 dark:text-orange-300'}`} />
                              <span className="truncate">
                                {flowLabel}: {formatAssigneeShortName(flow.assignee_name)}
                              </span>
                            </div>
                            {isFlowCompleted && (
                              <span className="inline-flex shrink-0 items-center gap-1 rounded bg-green-50 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-950/30 dark:text-green-300">
                                <CheckCircle className="h-2.5 w-2.5" />
                                <span>Завершено</span>
                              </span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="flex items-center gap-1.5 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50">
                        <UserCheck className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
                        <span className="truncate">{formatAssigneeShortName(result.assignee_name)}</span>
                      </div>
                    )}
                  </div>
                )}

                {result.order_id && (
                  <button
                    type="button"
                    onClick={openOrder}
                    className="route-order-action-button mt-1 inline-flex h-[34px] min-h-[34px] w-full items-center justify-center gap-1.5 rounded-lg border border-blue-500 bg-transparent px-3 text-xs font-semibold leading-none text-blue-700 transition-colors hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-900/20"
                  >
                    <ArrowRight className="h-3 w-3" />
                    <span>Перейти в заказ</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : hasSearched && !error ? (
          <div className="flex min-h-52 flex-col items-center justify-center px-6 text-center">
            <SearchX className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
            <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">Заказ не найден</div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Проверьте номер и повторите поиск</p>
          </div>
        ) : (
          <div className="flex min-h-52 flex-col items-center justify-center px-6 text-center">
            <Search className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
            <div className="text-sm font-medium text-gray-600 dark:text-gray-300">Введите номер заказа</div>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default OrderLookupModal;
